import React, { useState } from 'react';
import {
  Button,
  Modal,
  Form,
  Input,
  Select,
  Row,
  Col,
  Space,
  Tooltip,
  message,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  InfoCircleOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  FolderOpenOutlined,
} from '@ant-design/icons';

import SearchFilterInput from '../helpers/SearchFilterInput';
import MyTable from '../helpers/MyTable';
import SubPageLayout from '../helpers/SubPageLayout';
import SubPageHeader from '../helpers/SubPageHeader';

const { Option } = Select;
const { confirm } = Modal;

// 🔗 Demo: products currently at each contractor
// In future you can replace this with real data from backend
const contractorProductsMap = {
  'MineARC Fabrication': [
    {
      id: 1,
      productName: 'MineSAFE Modular Design',
      productCode: 'MineSAFE_Modular_Design',
      serialNumber: 'MAF1236',
      image: '/product_images/MineSAFE_Modular_Design.png',
      companyName: 'Apex Mining Ltd',
      businessManager: 'Sarah Johnson',
    },
    {
      id: 2,
      productName: 'TunnelSAFE Standard Design',
      productCode: 'TunnelSAFE_Standard_Design',
      serialNumber: 'MAF2231',
      image: '/product_images/TunnelSAFE_Standard_Design.png',
      companyName: 'Global Tunneling Co.',
      businessManager: 'Michael Chen',
    },
  ],
  'SteelWorks Pty Ltd': [
    {
      id: 3,
      productName: 'ChemSAFE Standard Design',
      productCode: 'ChemSAFE_Standard_Design',
      serialNumber: 'MAF1237',
      image: '/product_images/ChemSAFE_Standard_Design.png',
      companyName: 'SafeChem Industries',
      businessManager: 'Carlos Martinez',
    },
  ],
  'SurfaceCoat Solutions': [
    {
      id: 4,
      productName: 'CoalSAFE MSHA MS',
      productCode: 'CoalSAFE_MSHA_MS',
      serialNumber: 'MAF3312',
      image: '/product_images/CoalSAFE_MSHA_MS.png',
      companyName: 'CoalWorks Pty Ltd',
      businessManager: 'Emily Davis',
    },
  ],
  'PrimeBlast Services': [
    {
      id: 5,
      productName: 'StormSAFE Shelter',
      productCode: 'StormSAFE',
      serialNumber: 'MAF5515',
      image: '/product_images/StormSAFE.png',
      companyName: 'StormShield Corp.',
      businessManager: 'Olivia Brown',
    },
  ],
};

const Contractors = () => {
  const [form] = Form.useForm();

  const [contractors, setContractors] = useState([
    {
      id: 1,
      name: 'MineARC Fabrication',
      serviceType: 'Fabrication',
      contactPerson: 'John Smith',
      phone: '+27 11 555 1000',
      email: 'john.smith@minearcfab.com',
      address: 'Unit 4, Industrial Park, Johannesburg, South Africa',
    },
    {
      id: 2,
      name: 'SteelWorks Pty Ltd',
      serviceType: 'Fabrication',
      contactPerson: 'Amanda Lee',
      phone: '+27 11 555 2000',
      email: 'amanda.lee@steelworks.co.za',
      address: '12 Steel Avenue, Wadeville, Germiston, South Africa',
    },
    {
      id: 3,
      name: 'SurfaceCoat Solutions',
      serviceType: 'Paint & Blast',
      contactPerson: 'Peter Jones',
      phone: '+27 11 555 3000',
      email: 'peter.jones@surfacecoat.co.za',
      address: '45 Coatings Road, Boksburg, South Africa',
    },
    {
      id: 4,
      name: 'PrimeBlast Services',
      serviceType: 'Paint & Blast',
      contactPerson: 'Lerato Mokoena',
      phone: '+27 11 555 4000',
      email: 'lerato.m@primeblast.co.za',
      address: '8 Blast Street, Rustenburg, South Africa',
    },
  ]);

  const [searchQuery, setSearchQuery] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
  const [editingId, setEditingId] = useState(null);

  const [isDetailsVisible, setIsDetailsVisible] = useState(false);
  const [detailsContractor, setDetailsContractor] = useState(null);

  // Products modal state
  const [isProductsVisible, setIsProductsVisible] = useState(false);
  const [productsContractorName, setProductsContractorName] = useState('');
  const [productsForContractor, setProductsForContractor] = useState([]);

  const renderServiceTag = (type) => {
    let color = 'blue';
    if (type === 'Paint & Blast') color = 'purple';
    return <Tag color={color}>{type}</Tag>;
  };

  // ---------- TABLE COLUMNS ----------

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Service Type',
      dataIndex: 'serviceType',
      key: 'serviceType',
      render: (text) => renderServiceTag(text),
    },
    {
      title: 'Contact Person Names',
      dataIndex: 'contactPerson',
      key: 'contactPerson',
    },
    {
      title: 'Phone Number',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Physical Address',
      dataIndex: 'address',
      key: 'address',
      ellipsis: true,
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      render: (_, record) => {
        const iconStyle = {
          fontSize: 18,
          color: '#6b7280',
          cursor: 'pointer',
        };

        return (
          <Space size="middle" onClick={(e) => e.stopPropagation()}>
            <Tooltip title="Edit contractor">
              <EditOutlined
                style={iconStyle}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditClick(record);
                }}
              />
            </Tooltip>

            <Tooltip title="View details">
              <InfoCircleOutlined
                style={iconStyle}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDetailsClick(record);
                }}
              />
            </Tooltip>

            <Tooltip title="Open products at this contractor">
              <FolderOpenOutlined
                style={iconStyle}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenProducts(record);
                }}
              />
            </Tooltip>

            <Tooltip title="Remove contractor">
              <DeleteOutlined
                style={{ ...iconStyle, color: '#ef4444' }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteContractor(record.id);
                }}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  // Product table columns for the Products Modal
  const productColumns = [
    {
      title: 'Product',
      dataIndex: 'productName',
      key: 'productName',
      render: (text, record) => (
        <Space>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 12,
              overflow: 'hidden',
              background: '#f3f4f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {record.image ? (
              <img
                src={record.image}
                alt={text}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            ) : (
              <span style={{ fontSize: 11, color: '#9ca3af' }}>No Image</span>
            )}
          </div>
          <span>{text}</span>
        </Space>
      ),
    },
    {
      title: 'Product Code',
      dataIndex: 'productCode',
      key: 'productCode',
    },
    {
      title: 'Serial',
      dataIndex: 'serialNumber',
      key: 'serialNumber',
    },
    {
      title: 'Company Name',
      dataIndex: 'companyName',
      key: 'companyName',
    },
    {
      title: 'Business Manager',
      dataIndex: 'businessManager',
      key: 'businessManager',
    },
  ];

  // ---------- FILTERING ----------

  const filteredContractors = contractors.filter((c) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (c.name || '').toLowerCase().includes(q) ||
      (c.contactPerson || '').toLowerCase().includes(q)
    );
  });

  // ---------- MODAL HANDLERS ----------

  const resetModalState = () => {
    setEditingId(null);
    setModalMode('create');
    form.resetFields();
  };

  const showCreateModal = () => {
    resetModalState();
    setModalMode('create');
    setIsModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    resetModalState();
  };

  const handleEditClick = (record) => {
    setModalMode('edit');
    setEditingId(record.id);
    form.setFieldsValue({
      name: record.name,
      serviceType: record.serviceType,
      contactPerson: record.contactPerson,
      phone: record.phone,
      email: record.email,
      address: record.address,
    });
    setIsModalVisible(true);
  };

  const handleDetailsClick = (record) => {
    setDetailsContractor(record);
    setIsDetailsVisible(true);
  };

  const handleOpenProducts = (contractor) => {
    const products = contractorProductsMap[contractor.name] || [];
    setProductsForContractor(products);
    setProductsContractorName(contractor.name);
    setIsProductsVisible(true);
  };

  const handleDeleteContractor = (id) => {
    confirm({
      title: 'Remove this contractor?',
      icon: <ExclamationCircleOutlined />,
      content:
        'This contractor will be removed from your list. This action cannot be undone.',
      okText: 'Remove',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        setContractors((prev) => prev.filter((c) => c.id !== id));
        message.success('Contractor removed');
      },
    });
  };

  const handleSubmit = (values) => {
    if (modalMode === 'edit' && editingId != null) {
      setContractors((prev) =>
        prev.map((c) =>
          c.id === editingId
            ? {
                ...c,
                name: values.name,
                serviceType: values.serviceType,
                contactPerson: values.contactPerson,
                phone: values.phone,
                email: values.email,
                address: values.address,
              }
            : c
        )
      );
      message.success('Contractor updated successfully');
    } else {
      const newContractor = {
        id: Date.now(),
        name: values.name,
        serviceType: values.serviceType,
        contactPerson: values.contactPerson,
        phone: values.phone,
        email: values.email,
        address: values.address,
      };
      setContractors((prev) => [newContractor, ...prev]);
      message.success('Contractor added successfully');
    }

    handleModalCancel();
  };

  const isEditMode = modalMode === 'edit';

  return (
    <SubPageLayout
      title="Contractors"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={showCreateModal}>
          Add Contractor
        </Button>
      }
    >
      <SubPageHeader
        searchLoading={false}
        searchPlaceholder="Search Contractors by name or contact person"
        onSearch={(value) => setSearchQuery(value)}
        onChange={(e) => setSearchQuery(e.target.value)}
      />

      <MyTable
        columns={columns}
        dataSource={filteredContractors}
        rowKey="id"
      />

      {/* 🌟 Create / Edit Contractor Modal */}
      <Modal
        visible={isModalVisible}
        onCancel={handleModalCancel}
        title={null}
        footer={null}
        centered
        width={720}
        destroyOnClose
      >
        <div
          style={{
            padding: 24,
            borderRadius: 16,
            background:
              'linear-gradient(135deg, #ffffff 0%, #f3f4ff 40%, #eef7ff 100%)',
            boxShadow: '0 18px 45px rgba(15, 23, 42, 0.18)',
          }}
        >
          <div style={{ marginBottom: 18 }}>
            <div
              style={{
                fontSize: 20,
                fontWeight: 600,
                color: '#111827',
                marginBottom: 4,
              }}
            >
              {isEditMode ? 'Edit Contractor' : 'Add New Contractor'}
            </div>
            <div style={{ fontSize: 13, color: '#6b7280' }}>
              Maintain your contractor registry for Fabrication and Paint &amp; Blast services.
            </div>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            requiredMark="optional"
          >
            <Row gutter={16}>
              <Col span={14}>
                <Form.Item
                  label="Contractor Name"
                  name="name"
                  rules={[
                    { required: true, message: 'Please enter the contractor name' },
                  ]}
                >
                  <Input placeholder="e.g. MineARC Fabrication" />
                </Form.Item>
              </Col>
              <Col span={10}>
                <Form.Item
                  label="Service Type"
                  name="serviceType"
                  rules={[
                    { required: true, message: 'Please select a service type' },
                  ]}
                >
                  <Select placeholder="Select service type">
                    <Option value="Fabrication">Fabrication</Option>
                    <Option value="Paint & Blast">Paint &amp; Blast</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Contact Person Names"
                  name="contactPerson"
                  rules={[
                    { required: true, message: 'Please enter the contact person' },
                  ]}
                >
                  <Input placeholder="e.g. John Smith" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Phone Number"
                  name="phone"
                  rules={[
                    { required: true, message: 'Please enter a phone number' },
                  ]}
                >
                  <Input placeholder="e.g. +27 11 555 1000" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Email"
                  name="email"
                  rules={[
                    { required: true, message: 'Please enter an email address' },
                    { type: 'email', message: 'Please enter a valid email' },
                  ]}
                >
                  <Input placeholder="e.g. contact@company.co.za" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Physical Address"
                  name="address"
                  rules={[
                    { required: true, message: 'Please enter a physical address' },
                  ]}
                >
                  <Input placeholder="Street, City, Country" />
                </Form.Item>
              </Col>
            </Row>

            <div
              style={{
                marginTop: 16,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
              }}
            >
              <Button onClick={handleModalCancel}>Cancel</Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => form.submit()}
              >
                {isEditMode ? 'Save Changes' : 'Add Contractor'}
              </Button>
            </div>
          </Form>
        </div>
      </Modal>

      {/* 🔍 Contractor Details Modal */}
      <Modal
        visible={isDetailsVisible}
        onCancel={() => setIsDetailsVisible(false)}
        footer={null}
        centered
        width={640}
        title={null}
        destroyOnClose
      >
        {detailsContractor && (
          <div
            style={{
              padding: 24,
              borderRadius: 16,
              background: '#ffffff',
            }}
          >
            <div style={{ marginBottom: 12 }}>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  color: '#111827',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <span>{detailsContractor.name}</span>
                {renderServiceTag(detailsContractor.serviceType)}
              </div>
              <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
                {detailsContractor.address}
              </div>
            </div>

            <Row gutter={16} style={{ marginTop: 8 }}>
              <Col span={12}>
                <div style={{ marginBottom: 12 }}>
                  <div
                    style={{
                      fontSize: 11,
                      color: '#9ca3af',
                      textTransform: 'uppercase',
                    }}
                  >
                    Contact Person
                  </div>
                  <div style={{ fontSize: 14, color: '#111827' }}>
                    {detailsContractor.contactPerson}
                  </div>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <div
                    style={{
                      fontSize: 11,
                      color: '#9ca3af',
                      textTransform: 'uppercase',
                    }}
                  >
                    Phone Number
                  </div>
                  <div style={{ fontSize: 14, color: '#111827' }}>
                    {detailsContractor.phone}
                  </div>
                </div>
              </Col>

              <Col span={12}>
                <div style={{ marginBottom: 12 }}>
                  <div
                    style={{
                      fontSize: 11,
                      color: '#9ca3af',
                      textTransform: 'uppercase',
                    }}
                  >
                    Email
                  </div>
                  <div style={{ fontSize: 14, color: '#111827' }}>
                    {detailsContractor.email}
                  </div>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <div
                    style={{
                      fontSize: 11,
                      color: '#9ca3af',
                      textTransform: 'uppercase',
                    }}
                  >
                    Physical Address
                  </div>
                  <div style={{ fontSize: 14, color: '#111827' }}>
                    {detailsContractor.address}
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        )}
      </Modal>

      {/* 📦 Products at Contractor Modal */}
      <Modal
        visible={isProductsVisible}
        onCancel={() => setIsProductsVisible(false)}
        footer={null}
        centered
        width={900}
        title={null}
        destroyOnClose
      >
        <div
          style={{
            padding: 24,
            borderRadius: 16,
            background: '#ffffff',
          }}
        >
          <div style={{ marginBottom: 12 }}>
            <div
              style={{
                fontSize: 20,
                fontWeight: 600,
                color: '#111827',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <span>Products at {productsContractorName}</span>
              <Tag>
                {productsForContractor.length} product
                {productsForContractor.length === 1 ? '' : 's'}
              </Tag>
            </div>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
              Listing all products currently assigned to this contractor.
            </div>
          </div>

          {productsForContractor.length > 0 ? (
            <MyTable
              columns={productColumns}
              dataSource={productsForContractor}
              rowKey="id"
            />
          ) : (
            <div
              style={{
                marginTop: 16,
                fontSize: 13,
                color: '#9ca3af',
              }}
            >
              No products are currently assigned to this contractor.
            </div>
          )}
        </div>
      </Modal>
    </SubPageLayout>
  );
};

export default Contractors;
